import { DropDownList } from '@progress/kendo-react-dropdowns';
import type { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { useNavigate } from 'react-router-dom';
import type { RegionCode } from '@shared/api-types';
import { useRegion } from '../lib/regionContext';
import { useAdminAuth } from '../lib/adminAuth';

const OPTIONS: { value: RegionCode; text: string }[] = [
  { value: 'dv', text: 'DV (dev)' },
  { value: 'us', text: 'US' },
  { value: 'eu', text: 'EU' },
];

/**
 * Region picker shown on every authenticated page header. Per the Phase 5c
 * spec: switching regions clears the in-app cache, re-authenticates against
 * that region's admin host, and reloads all data. v1 implementation:
 * persists the new region to localStorage, signs out, and navigates to
 * /login with a notice. The "switch hosts" behaviour is implicit in
 * production where each region has its own admin SWA / host (and so the
 * picker is really useful only as a navigation aid before sign-in); in
 * dev there's a single backend and the picker is a no-op for network
 * calls — documented in docs/architecture.md.
 */
export function RegionPicker() {
  const { region, setRegion } = useRegion();
  const { signOut } = useAdminAuth();
  const navigate = useNavigate();

  const current = OPTIONS.find((o) => o.value === region) ?? OPTIONS[0];

  const onChange = (e: DropDownListChangeEvent) => {
    const next = (e.target.value as { value: RegionCode } | null)?.value ?? 'dv';
    if (next === region) return;
    setRegion(next);
    // Re-auth required per the spec (separate PlatformAdminUser row per region).
    void signOut().then(() =>
      navigate('/login', { state: { regionSwitch: next }, replace: true }),
    );
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 12, color: '#6b7280' }}>Region</span>
      <DropDownList
        data={OPTIONS}
        textField="text"
        dataItemKey="value"
        value={current}
        onChange={onChange}
        style={{ width: 140 }}
      />
    </div>
  );
}
